import { useState, useEffect, useMemo } from 'react';
import { DollarSign, Calendar, FileText, Send, Save, Lock, TriangleAlert, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { BackLink } from '@/components/shared/DashboardUI';
import {
  formatCurrency as formatCurrencyInput, parseCurrency, todayInAppZone,
  formatBandPeriodLabel, formatBandDateRangeLabel,
} from '@/lib/formatters';
import { addCalendarMonths } from '@/lib/calendarMonths';
import {
  DEFAULT_INTERVAL, DEFAULT_FEE_MODEL, DEFAULT_PROCESSING_FEE, PRICING_VERSION,
  VALID_INTERVALS, VALID_FEE_MODELS, computeBands, resolveFees,
} from '@/lib/offerPricing';
import '@/styles/authenticated.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Currency display with forced 2-decimal precision (for offer math display)
const formatCurrencyDisplay = (value) => {
  if (!value && value !== 0) return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.]/g, '')) : value;
  if (isNaN(num)) return '';
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Starting points only. These are suggestions the funder may take or ignore —
// they are NOT limits, and they are not shown to the plaintiff: CompareOffers
// defaults showRisk=false and only AdminDashboard passes it, so risk_tier is
// an internal label. Applying one never overwrites a field the funder edited.
const RISK_TIERS = {
  low: { rate: 24, capMultiplier: 2.0, label: 'Low' },
  medium: { rate: 32, capMultiplier: 2.5, label: 'Medium' },
  high: { rate: 38, capMultiplier: 3.0, label: 'High' }
};

const DEFAULT_TIER = 'medium';

// Offers a funder can still act on. Matches LIVE_OFFER_STATUSES on the server:
// draft and submitted are editable, everything else is settled.
const LIVE_OFFER_STATUSES = ['draft', 'submitted'];

// The funder's standing broker fee for a given advance. Defined once because
// the draft-resume path has to ask the same question the offer math asks —
// "is the stored fee just the default?" — and a second copy would drift.
const standingBrokerFee = (advance, pct, cap) => {
  let fee = (advance || 0) * pct;
  if (cap !== null && cap !== undefined && cap > 0) fee = Math.min(fee, cap);
  return fee;
};

// Money compared at cent precision; floats make === unsafe here.
const sameMoney = (a, b) => Math.abs(Number(a) - Number(b)) < 0.005;

const FunderOfferBuilder = ({ application, onClose, onOfferCreated, onNavigate, getAuthHeaders, funderSettings, funderUser }) => {
  const originalRequestedAmount = parseFloat(application?.requestedAmount || application?.requested_amount || 0);
  const settings = funderSettings || {};
  const brokerFeePct = (funderUser?.broker_fee_percentage ?? 15) / 100;
  const brokerFeeCap = funderUser?.broker_fee_cap ?? null;
  // Admin can override per funder; null/undefined → platform default $99. 0 = waived.
  const defaultApplicationFee = (funderUser?.application_fee === null || funderUser?.application_fee === undefined)
    ? 99
    : Number(funderUser.application_fee);

  // Whether this application's attorney is on the platform-fee waiver list.
  // Decided server-side from PLATFORM_FEE_WAIVED_LAWYER_EMAILS and published on
  // the application detail, because the tier schedule carries no waiver
  // information. Without it this preview charged a firm that create_offer
  // exempts, and every figure below — total fees, total funded, the cap, the
  // repayment schedule — was overstated by the tier fee.
  const platformFeeWaived = !!application?.platform_fee_waived;

  // Platform fee tiers — admin-configured, loaded once. We resolve the matching
  // tier from `advanceAmount` and add it to the offer math.
  const [platformFeeTiers, setPlatformFeeTiers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/platform-fee-tiers`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setPlatformFeeTiers(data.tiers || []);
      } catch (e) { /* ignore — platform_fee falls back to 0 */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const [formData, setFormData] = useState({
    advanceAmount: originalRequestedAmount > 0 ? originalRequestedAmount.toString() : '',
    brokerFee: '',
    applicationFee: '',
    // Seeded from the default preset rather than from the funder's ceilings.
    // The old code seeded from settings and then let an effect immediately
    // overwrite it; seeding from the preset states the same result honestly.
    annualizedRate: RISK_TIERS[DEFAULT_TIER].rate.toString(),
    fundingDate: todayInAppZone(),
    capMultiplier: RISK_TIERS[DEFAULT_TIER].capMultiplier.toString(),
    maxTermMonths: (settings.max_term_months || 48).toString(),
    maximumCap: '',
    riskTier: DEFAULT_TIER,
    notes: '',
    // These four are the funder's own pricing preferences, so — unlike
    // maxTermMonths above — they seed from `funderUser` (the `/users/me`
    // document), not `settings` (the `/funder/settings` browse ceilings).
    // `??`, never `||`: a funder can legitimately set 0 or false, and `||`
    // would silently replace either with the platform default.
    accrualIntervalMonths: (funderUser?.default_accrual_interval_months ?? DEFAULT_INTERVAL).toString(),
    feesAccrueInterest: funderUser?.default_fees_accrue_interest !== false,
    feeModel: funderUser?.default_fee_model ?? DEFAULT_FEE_MODEL,
    processingFee: (funderUser?.processing_fee ?? DEFAULT_PROCESSING_FEE).toString()
  });

  // Fields the funder has typed into. A preset skips these, so clicking a tier
  // to compare it can no longer destroy a hand-tuned number with no undo.
  const [edited, setEdited] = useState({});

  // Standing fees are the funder's own defaults. They stay read-only until the
  // funder opens an override, which applies to THIS offer only — nothing here
  // ever writes back to PUT /users/me.
  const [feeOverride, setFeeOverride] = useState({ brokerFee: false, applicationFee: false, processingFee: false });

  const [loading, setLoading] = useState(false);
  const [useAutoCapCalculation, setUseAutoCapCalculation] = useState(true);
  const [scheduleExpanded, setScheduleExpanded] = useState(false);

  // A funder has one live offer per application, so opening one EDITS it —
  // draft or submitted alike. Treating a submitted offer as a blank slate is
  // what let "Make Another Offer" write a second row the plaintiff would
  // never see.
  const editingLiveOffer = application?.offer_status === 'submitted';
  const resumableOfferId = (LIVE_OFFER_STATUSES.includes(application?.offer_status) && application?.offer_id)
    ? application.offer_id
    : null;
  const [draftOfferId, setDraftOfferId] = useState(resumableOfferId);
  // The offer document as STORED, kept whole rather than only spread into
  // formData, because a v1 offer is rendered from it directly. Null until
  // the load below resolves, and for a brand-new offer forever.
  const [storedOffer, setStoredOffer] = useState(null);

  // Load draft offer data if resuming
  useEffect(() => {
    if (LIVE_OFFER_STATUSES.includes(application?.offer_status) && application?.offer_id) {
      (async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/offers/${application.offer_id}`, { credentials: 'include', headers: getAuthHeaders() });
          if (response.ok) {
            const draft = await response.json();
            setStoredOffer(draft);
            const tier = draft.risk_tier || DEFAULT_TIER;
            setFormData({
              advanceAmount: draft.advance_amount ? draft.advance_amount.toString() : '',
              brokerFee: draft.broker_fee !== undefined && draft.broker_fee !== null ? Number(draft.broker_fee).toFixed(2) : '',
              applicationFee: draft.application_fee !== undefined && draft.application_fee !== null
                ? Number(draft.application_fee).toFixed(2)
                : '',
              annualizedRate: draft.annualized_rate ? draft.annualized_rate.toString() : RISK_TIERS[tier].rate.toString(),
              fundingDate: draft.funding_date || todayInAppZone(),
              capMultiplier: draft.cap_multiplier ? draft.cap_multiplier.toString() : RISK_TIERS[tier].capMultiplier.toString(),
              maxTermMonths: draft.max_term_months ? draft.max_term_months.toString() : (settings.max_term_months || 48).toString(),
              maximumCap: draft.maximum_cap ? draft.maximum_cap.toString() : '',
              riskTier: tier,
              notes: draft.notes || '',
              // Same seeding rule as the initial state: fall back to the
              // funder's own saved preference, never the settings ceilings.
              accrualIntervalMonths: (draft.accrual_interval_months
                ?? funderUser?.default_accrual_interval_months
                ?? DEFAULT_INTERVAL).toString(),
              feesAccrueInterest: draft.fees_accrue_interest !== undefined && draft.fees_accrue_interest !== null
                ? !!draft.fees_accrue_interest
                : funderUser?.default_fees_accrue_interest !== false,
              feeModel: draft.fee_model || funderUser?.default_fee_model || DEFAULT_FEE_MODEL,
              processingFee: draft.processing_fee !== undefined && draft.processing_fee !== null
                ? Number(draft.processing_fee).toFixed(2)
                : ''
            });
            // Mark only what genuinely differs from this offer's own preset.
            // Flagging all three unconditionally told a funder opening an
            // untouched Low-risk offer that the rate was EDITED, and offered a
            // Reset to 24% — the value already on screen.
            const preset = RISK_TIERS[tier] || RISK_TIERS[DEFAULT_TIER];
            const storedRate = draft.annualized_rate;
            const storedCap = draft.cap_multiplier;
            setEdited({
              annualizedRate: storedRate !== undefined && storedRate !== null && Number(storedRate) !== preset.rate,
              capMultiplier: storedCap !== undefined && storedCap !== null && Number(storedCap) !== preset.capMultiplier,
            });
            // The server records whether each fee was overridden, so read that
            // rather than guessing. Comparing the stored amount against today's
            // default only agrees by coincidence: change your percentage after
            // overriding a draft and the override becomes indistinguishable
            // from a default, then vanishes on the next save.
            //
            // The comparison stays as the fallback for offers created before
            // the flag existed, where the amount is the only evidence there is.
            const draftAdvance = parseFloat(draft.advance_amount) || 0;
            const brokerDefault = standingBrokerFee(draftAdvance, brokerFeePct, brokerFeeCap);
            const brokerStored = draft.broker_fee;
            const brokerOverridden = draft.broker_fee_overridden !== undefined
              ? !!draft.broker_fee_overridden
              : (brokerStored !== undefined && brokerStored !== null && !sameMoney(brokerStored, brokerDefault));
            const appStored = draft.application_fee;
            const appOverridden = draft.application_fee_overridden !== undefined
              ? !!draft.application_fee_overridden
              : (appStored !== undefined && appStored !== null && !sameMoney(appStored, defaultApplicationFee));
            const defaultProcessingFee = funderUser?.processing_fee ?? DEFAULT_PROCESSING_FEE;
            const processingStored = draft.processing_fee;
            const processingOverridden = draft.processing_fee_overridden !== undefined
              ? !!draft.processing_fee_overridden
              : (processingStored !== undefined && processingStored !== null && !sameMoney(processingStored, defaultProcessingFee));
            setFeeOverride({ brokerFee: brokerOverridden, applicationFee: appOverridden, processingFee: processingOverridden });
            setDraftOfferId(draft.offer_id);
            // Every stored offer carries maximum_cap, so treating its presence
            // as an override labelled every resumed offer "Set by hand for this
            // offer". Only a cap that differs from the auto figure was set by
            // hand.
            if (draft.maximum_cap) {
              const advance = parseFloat(draft.advance_amount) || 0;
              const draftFeeModel = draft.fee_model || funderUser?.default_fee_model || DEFAULT_FEE_MODEL;
              let funderFeesTotal;
              if (draftFeeModel === 'flat') {
                funderFeesTotal = processingStored !== undefined && processingStored !== null
                  ? Number(processingStored)
                  : defaultProcessingFee;
              } else {
                const broker = draft.broker_fee !== undefined && draft.broker_fee !== null
                  ? Number(draft.broker_fee)
                  : standingBrokerFee(advance, brokerFeePct, brokerFeeCap);
                const appFee = draft.application_fee !== undefined && draft.application_fee !== null
                  ? Number(draft.application_fee)
                  : defaultApplicationFee;
                funderFeesTotal = broker + appFee;
              }
              const platform = Number(draft.platform_fee) || 0;
              const autoCap = (advance + funderFeesTotal + platform) * (Number(draft.cap_multiplier) || 2.5);
              setUseAutoCapCalculation(sameMoney(draft.maximum_cap, autoCap));
            }
          }
        } catch (error) { console.error('Failed to load draft:', error); }
      })();
    }
  }, []);

  const calculatedBrokerFee = useMemo(() => {
    // Admin-controlled: pct can be 0 (no broker fee). No platform-side floor — if
    // the admin sets 0%, the broker fee is $0.
    const advance = parseFloat(parseCurrency(formData.advanceAmount)) || 0;
    return standingBrokerFee(advance, brokerFeePct, brokerFeeCap);
  }, [formData.advanceAmount, brokerFeePct, brokerFeeCap]);

  // What each fee actually contributes to this offer: the funder's standing
  // default, unless they opened an override for this one. Used for the
  // broker-fee input's own clamp warning below — resolveFees applies the
  // same clamp itself, but the warning needs the raw typed value, not the
  // clamped one.
  const overriddenBrokerFee = parseFloat(parseCurrency(formData.brokerFee)) || 0;
  const brokerFeeClamped = feeOverride.brokerFee
    && brokerFeeCap !== null && brokerFeeCap > 0 && overriddenBrokerFee > brokerFeeCap;

  // Fed to resolveFees below rather than recomputed inline. Building this
  // as the same snake_case shape the offer submission and the backend's
  // resolve_fees both use means one set of field names, not a second one
  // invented for the preview.
  const feeSubmission = useMemo(() => ({
    broker_fee: overriddenBrokerFee,
    broker_fee_overridden: feeOverride.brokerFee,
    application_fee: parseFloat(parseCurrency(formData.applicationFee)) || 0,
    application_fee_overridden: feeOverride.applicationFee,
    processing_fee: parseFloat(parseCurrency(formData.processingFee)) || 0,
    processing_fee_overridden: feeOverride.processingFee,
  }), [overriddenBrokerFee, feeOverride, formData.applicationFee, formData.processingFee]);

  // Platform fee depends only on the advance, never on the fee model, so it
  // resolves independently and is handed to resolveFees to fold into its
  // total.
  const platformFee = useMemo(() => {
    const advance = parseFloat(parseCurrency(formData.advanceAmount)) || 0;
    if (platformFeeWaived || advance <= 0 || !platformFeeTiers.length) return 0;
    const t = platformFeeTiers.find(t => (t.max_amount === null && advance >= (t.min_amount || 0)) || (t.max_amount !== null && advance >= (t.min_amount || 0) && advance <= t.max_amount));
    return t ? Number(t.fee_amount) || 0 : 0;
  }, [formData.advanceAmount, platformFeeTiers, platformFeeWaived]);

  // The single place fee totals are computed. Routing through the shared
  // engine — rather than recomputing broker/application/processing totals
  // inline — is what makes the fee model actually change the displayed
  // totals: inline recomputation is how the preview and the stored offer
  // have diverged before.
  const resolvedFees = useMemo(() => {
    const advance = parseFloat(parseCurrency(formData.advanceAmount)) || 0;
    return resolveFees({
      advance,
      feeModel: formData.feeModel,
      funder: funderUser || {},
      submitted: feeSubmission,
      platformFee,
    });
  }, [formData.advanceAmount, formData.feeModel, funderUser, feeSubmission, platformFee]);

  const fundingSummary = useMemo(() => {
    const advance = parseFloat(parseCurrency(formData.advanceAmount)) || 0;
    return {
      advance,
      broker: resolvedFees.broker_fee,
      appFee: resolvedFees.application_fee,
      processingFee: resolvedFees.processing_fee,
      platformFee,
      totalFees: resolvedFees.total_fees,
      totalFunded: advance + resolvedFees.total_fees,
    };
  }, [formData.advanceAmount, resolvedFees, platformFee]);

  // A STORED offer's pricing version, not whatever it happens to carry: an
  // offer written before this work has no `pricing_version` at all, so
  // anything short of the current version reads as v1 rather than as some
  // other v2 variant. Same rule as CompareOffers and FunderDashboard —
  // spec §4 requires all three schedule consumers to branch here, and this
  // one did not.
  //
  // A v1 offer is FROZEN. update_offer strips every pricing field out of the
  // write, so a v2 payment-window preview built for it is a preview of terms
  // that can never be stored — the exact preview-vs-stored split this module
  // exists to close, and the path that made the freeze hole reachable. So the
  // builder does not present a v1 offer as editable pricing at all: it shows
  // the stored figures and the stored dated schedule, read-only, and saves
  // only the notes.
  const frozenV1 = !!storedOffer && storedOffer.pricing_version !== PRICING_VERSION;

  // Figures for a frozen offer come off the DOCUMENT, never off the form.
  // Recomputing them would put a number on screen that the server will not
  // store — which is the whole failure this branch prevents.
  const storedSummary = useMemo(() => {
    if (!frozenV1) return null;
    const advance = Number(storedOffer.advance_amount) || 0;
    const broker = Number(storedOffer.broker_fee) || 0;
    const appFee = Number(storedOffer.application_fee) || 0;
    const processingFee = Number(storedOffer.processing_fee) || 0;
    const platform = Number(storedOffer.platform_fee) || 0;
    const totalFees = typeof storedOffer.total_fees === 'number'
      ? storedOffer.total_fees
      : broker + appFee + processingFee + platform;
    return {
      advance,
      broker,
      appFee,
      processingFee,
      platformFee: platform,
      totalFees,
      totalFunded: typeof storedOffer.total_funded_amount === 'number'
        ? storedOffer.total_funded_amount
        : advance + totalFees,
    };
  }, [frozenV1, storedOffer]);

  const maximumCap = useMemo(() => {
    if (!useAutoCapCalculation && formData.maximumCap) return parseFloat(parseCurrency(formData.maximumCap)) || 0;
    // Option A: Cap is based on total financed obligation (advance + fees)
    const obligation = fundingSummary.totalFunded;
    const multiplier = parseFloat(formData.capMultiplier) || 2.5;
    return obligation * multiplier;
  }, [fundingSummary.totalFunded, formData.capMultiplier, formData.maximumCap, useAutoCapCalculation]);

  // What the rail actually renders. One name, so no call site below has to
  // remember which version it is looking at.
  const displaySummary = storedSummary || fundingSummary;
  const displayCap = frozenV1 ? (Number(storedOffer.maximum_cap) || 0) : maximumCap;
  const storedSchedule = frozenV1 ? (storedOffer.repayment_schedule || []) : [];

  const repaymentBands = useMemo(() => {
    // Option A: Interest accrues on the full financed obligation (advance + all fees)
    const obligation = fundingSummary.totalFunded;
    const rate = parseFloat(formData.annualizedRate) || 0;
    // A funding date is a plain calendar date, so the schedule is computed as
    // plain calendar dates and never becomes an instant. The old code anchored
    // at noon UTC and stepped with setUTCMonth, which is calendar-aware but
    // OVERFLOWS at month end: funding on Aug 31 previewed Mar 3 rather than
    // Feb 28. addCalendarMonths clamps, and matches what create_offer stores.
    const maxTerm = parseInt(formData.maxTermMonths, 10) || 48;

    // No `!rate` guard: a 0% fee-only offer is legitimate and previewing it
    // as an empty rail is how the browser and the server disagreed before.
    if (!obligation || !addCalendarMonths(formData.fundingDate, 0)) return [];

    return computeBands({
      advance: fundingSummary.advance,
      fees: obligation - fundingSummary.advance,
      annualizedRate: rate,
      maximumCap: maximumCap,
      maxTermMonths: maxTerm,
      accrualIntervalMonths: parseInt(formData.accrualIntervalMonths, 10) || DEFAULT_INTERVAL,
      feesAccrueInterest: formData.feesAccrueInterest,
      fundingDate: formData.fundingDate,
    });
  }, [
    fundingSummary.advance,
    fundingSummary.totalFunded,
    formData.annualizedRate,
    formData.accrualIntervalMonths,
    formData.feesAccrueInterest,
    formData.fundingDate,
    formData.maxTermMonths,
    maximumCap,
  ]);

  // The repayment line plotted against the cap, so the cap reads as a ceiling
  // the offer approaches rather than a number sitting on its own.
  const curve = useMemo(() => {
    // A frozen v1 offer has no bands to plot and no preview to justify one:
    // its schedule is the stored dated table, rendered below.
    if (frozenV1) return null;
    if (repaymentBands.length === 0 || maximumCap <= 0) return null;
    const floor = fundingSummary.totalFunded;
    const span = maximumCap - floor;
    if (span <= 0) return null;
    const y = (amount) => 110 - Math.max(0, Math.min(1, (amount - floor) / span)) * 100;
    const lastIndex = Math.max(1, repaymentBands.length - 1);
    const points = repaymentBands.map((row, i) => ({
      x: 10 + (i / lastIndex) * 300,
      y: y(row.amount)
    }));
    return {
      line: points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' '),
      area: `${points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ')} L310 118 L10 118 Z`,
      first: points[0],
      last: points[points.length - 1],
      capReached: repaymentBands.some((r) => r.is_cap),
      firstMonths: repaymentBands[0].to_month,
      lastMonths: repaymentBands[repaymentBands.length - 1].to_month
    };
  }, [frozenV1, repaymentBands, maximumCap, fundingSummary.totalFunded]);

  const handleInputChange = (field, value) => { setFormData(prev => ({ ...prev, [field]: value })); };

  // Typing into a preset-filled field claims it: presets stop touching it and a
  // reset control appears.
  const handleTermChange = (field, value) => {
    setEdited(prev => ({ ...prev, [field]: true }));
    handleInputChange(field, value);
  };

  const applyPreset = (tier) => {
    const preset = RISK_TIERS[tier];
    setFormData(prev => ({
      ...prev,
      riskTier: tier,
      annualizedRate: edited.annualizedRate ? prev.annualizedRate : preset.rate.toString(),
      capMultiplier: edited.capMultiplier ? prev.capMultiplier : preset.capMultiplier.toString()
    }));
  };

  const resetToPreset = (field) => {
    const preset = RISK_TIERS[formData.riskTier] || RISK_TIERS[DEFAULT_TIER];
    const presetValue = field === 'annualizedRate' ? preset.rate : preset.capMultiplier;
    setEdited(prev => { const next = { ...prev }; delete next[field]; return next; });
    handleInputChange(field, presetValue.toString());
  };

  const openOverride = (field, seed) => {
    setFeeOverride(prev => ({ ...prev, [field]: true }));
    handleInputChange(field, seed.toFixed(2));
  };

  const revertOverride = (field) => {
    setFeeOverride(prev => ({ ...prev, [field]: false }));
    handleInputChange(field, '');
  };

  // A funder's saved ceilings are context, not gates. We say when a value has
  // passed one and point at Settings; we never clamp or block the submit.
  const rateCeiling = settings.max_annualized_rate;
  const overRateCeiling = rateCeiling !== undefined && rateCeiling !== null
    && (parseFloat(formData.annualizedRate) || 0) > rateCeiling;
  const capCeiling = settings.max_cap_rate;
  const overCapCeiling = capCeiling !== undefined && capCeiling !== null
    && (parseFloat(formData.capMultiplier) || 0) > capCeiling;

  const handleSaveOffer = async (submit = false) => {
    if (!formData.advanceAmount || parseFloat(parseCurrency(formData.advanceAmount)) <= 0) { toast.error('Please enter an advance amount'); return; }
    setLoading(true);
    try {
      // A v1 offer is frozen. update_offer strips every pricing field out of
      // the write, so sending them would be theatre — the funder would watch
      // a save succeed and nothing change. Notes is what is genuinely left.
      const payload = frozenV1 ? { notes: formData.notes } : {
        application_id: application.application_id,
        advance_amount: parseFloat(parseCurrency(formData.advanceAmount)),
        broker_fee: resolvedFees.broker_fee,
        application_fee: resolvedFees.application_fee,
        // Without these the server recomputes both fees from the funder's
        // saved settings and the override never lands.
        broker_fee_overridden: feeOverride.brokerFee,
        application_fee_overridden: feeOverride.applicationFee,
        // The server 400s an override flag with no amount behind it, so this
        // only ever ships true alongside the resolved amount that justifies
        // it — never a bare flag.
        processing_fee: resolvedFees.processing_fee,
        processing_fee_overridden: feeOverride.processingFee,
        accrual_interval_months: parseInt(formData.accrualIntervalMonths, 10) || DEFAULT_INTERVAL,
        fees_accrue_interest: formData.feesAccrueInterest,
        fee_model: formData.feeModel,
        annualized_rate: parseFloat(formData.annualizedRate),
        funding_date: formData.fundingDate,
        cap_multiplier: parseFloat(formData.capMultiplier),
        maximum_cap: useAutoCapCalculation ? null : parseFloat(parseCurrency(formData.maximumCap)),
        risk_tier: formData.riskTier,
        max_term_months: parseInt(formData.maxTermMonths) || 48,
        notes: formData.notes
      };

      let offerId = draftOfferId;

      if (draftOfferId) {
        // Update existing draft
        const response = await fetch(`${API}/offers/${draftOfferId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, credentials: 'include',
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          const offer = await response.json();
          offerId = offer.offer_id || draftOfferId;
          if (editingLiveOffer) {
            // Already submitted. POST /offers/{id}/submit only accepts drafts,
            // so firing it here returned a 400 and told the funder their live
            // offer had been "saved as draft" — false in both halves.
            toast.success('Offer updated. The plaintiff sees the new terms.');
          } else if (submit) {
            const submitResponse = await fetch(`${API}/offers/${offerId}/submit`, { method: 'POST', headers: getAuthHeaders(), credentials: 'include' });
            if (submitResponse.ok) toast.success('Offer created and submitted!');
            else toast.success('Offer updated as draft');
          } else { toast.success('Draft offer updated'); }
          if (onOfferCreated) onOfferCreated(offer);
          onClose();
        } else { const error = await response.json(); toast.error(error.detail || 'Failed to update offer'); }
      } else {
        // Create new offer
        const response = await fetch(`${API}/offers`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, credentials: 'include',
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          const offer = await response.json();
          offerId = offer.offer_id;
          setDraftOfferId(offerId);
          if (submit) {
            const submitResponse = await fetch(`${API}/offers/${offerId}/submit`, { method: 'POST', headers: getAuthHeaders(), credentials: 'include' });
            if (submitResponse.ok) toast.success('Offer created and submitted!');
            else toast.success('Offer saved as draft');
          } else { toast.success('Offer saved as draft'); }
          if (onOfferCreated) onOfferCreated(offer);
          onClose();
        } else { const error = await response.json(); toast.error(error.detail || 'Failed to create offer'); }
      }
    } catch (error) { toast.error('Failed to save offer'); }
    finally { setLoading(false); }
  };

  // ---- shared bits of the ownership vocabulary ----

  const badgeBase = {
    display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '0.15rem 0.5rem',
    borderRadius: '5px', fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.04em'
  };
  const thisOfferBadge = { ...badgeBase, backgroundColor: 'var(--mcl-success-bg)', color: 'var(--mcl-success)' };
  const yourDefaultBadge = { ...badgeBase, backgroundColor: 'var(--mcl-info-bg)', color: 'var(--mcl-primary)' };
  const platformBadge = { ...badgeBase, backgroundColor: 'var(--mcl-border-light)', color: '#475569' };

  const overrideButtonStyle = {
    position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
    display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.2rem 0.5rem',
    fontSize: '0.6875rem', fontWeight: 700, fontFamily: 'inherit', borderRadius: '6px',
    border: '1px solid #bfdbfe', backgroundColor: 'var(--mcl-info-bg)', color: 'var(--mcl-primary)',
    cursor: 'pointer', lineHeight: 1.4
  };
  const linkButtonStyle = {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    color: 'var(--mcl-primary)', fontWeight: 600, fontSize: '0.75rem', fontFamily: 'inherit'
  };
  const standingFeeInputStyle = (open) => ({
    paddingLeft: '28px',
    paddingRight: open ? '0.85rem' : '5.5rem',
    backgroundColor: open ? '#ffffff' : '#f8fafc',
    borderStyle: open ? 'solid' : 'dashed',
    borderColor: open ? '#93c5fd' : '#cbd5e1',
    fontWeight: open ? 600 : 400
  });

  const renderStandingFee = ({ field, testId, label, value, hint, seed }) => {
    // A frozen v1 offer's fees are the stored ones and nothing here can move
    // them, so the override affordance is not offered rather than offered and
    // ignored.
    const open = !frozenV1 && feeOverride[field];
    return (
      <div className="mcl-form-group" style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '0.4rem' }}>
          <label className="mcl-form-label" style={{ marginBottom: 0 }}>{label}</label>
          <span style={yourDefaultBadge}>{frozenV1 ? 'AS AGREED' : open ? 'OVERRIDDEN' : 'YOUR DEFAULT'}</span>
        </div>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)', fontWeight: 500 }}>$</span>
          <input
            className="mcl-form-input"
            type="text"
            value={value}
            readOnly={!open}
            onChange={(e) => handleInputChange(field, parseCurrency(e.target.value))}
            style={standingFeeInputStyle(open)}
            data-testid={testId}
          />
          {!open && !frozenV1 && (
            <button type="button" onClick={() => openOverride(field, seed)} style={overrideButtonStyle} data-testid={`override-${testId.replace('input-', '')}`}>
              Override <ChevronDown size={11} />
            </button>
          )}
        </div>
        <div className="mcl-form-hint" style={{ color: 'var(--mcl-text-muted)', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span>{hint}</span>
          {open ? (
            <button type="button" onClick={() => revertOverride(field)} style={linkButtonStyle} data-testid={`revert-${testId.replace('input-', '')}`}>
              Revert to default
            </button>
          ) : frozenV1 ? null : (
            <button type="button" onClick={() => goToSettings()} style={linkButtonStyle} data-testid={`settings-${testId.replace('input-', '')}`}>
              Change in Settings
            </button>
          )}
        </div>
        {open && (
          <div className="mcl-form-hint" style={{ color: 'var(--mcl-primary)' }}>
            This offer only &mdash; your saved default is unchanged.
          </div>
        )}
        {field === 'brokerFee' && brokerFeeClamped && (
          <div className="mcl-form-hint" style={{ color: 'var(--mcl-warning)', display: 'flex', alignItems: 'flex-start', gap: '5px' }} data-testid="broker-fee-clamped">
            <TriangleAlert size={13} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>Capped at your ${brokerFeeCap.toLocaleString()} broker fee limit.</span>
          </div>
        )}
      </div>
    );
  };

  // A row is a BAND — one amount owed anywhere inside a window, not an
  // amount pinned to a single date — so the period ("0–6 months") leads and
  // the exact from/to dates sit beneath it. The terminal band's `to` is
  // null and reads as open-ended rather than as a blank or a date.
  //
  // The badge follows `is_cap`, never `is_terminal`: every schedule's last
  // band is terminal, but only some are terminal BECAUSE the cap was hit. A
  // term that runs out first is final without being capped, and labelling
  // it "(cap)" would state something false.
  const renderBandRow = (item, index) => (
    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }} data-testid={`band-row-${index}`}>
      <div>
        <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }} data-testid="band-period">
          {formatBandPeriodLabel(item.from_month, item.to_month)}
        </div>
        <div style={{ color: 'var(--mcl-text-light)', fontSize: '0.6875rem', marginTop: '0.1rem' }} data-testid="band-dates">
          {formatBandDateRangeLabel(item.from, item.to)}
          {item.is_cap && <span style={{ marginLeft: '0.4rem', color: 'var(--mcl-warning)', fontWeight: 600 }}>(cap)</span>}
        </div>
      </div>
      <span style={{ fontWeight: 600, color: 'var(--mcl-text-heading)', whiteSpace: 'nowrap' }} data-testid="band-amount">${formatCurrencyDisplay(item.amount)}</span>
    </div>
  );

  // Expanded, group bands by year so a short accrual interval over a long
  // term (48 monthly bands) reads as a handful of sections rather than one
  // wall of rows. Collapsed always shows the first three regardless of
  // interval, so grouping only applies to the expanded view.
  const bandYearGroups = useMemo(() => {
    const groups = [];
    repaymentBands.forEach((band, index) => {
      const year = Math.floor(band.from_month / 12);
      let group = groups.find((g) => g.year === year);
      if (!group) { group = { year, items: [] }; groups.push(group); }
      group.items.push({ band, index });
    });
    return groups;
  }, [repaymentBands]);

  // Settings is a sibling tab of the funder dashboard. Telling a funder a fee is
  // theirs to change and then not taking them there is most of the original
  // problem, so this navigates when the parent gave us a way to.
  const goToSettings = () => {
    if (onNavigate) onNavigate('settings');
    else toast.success('Your fee defaults live in Settings');
  };

  return (
    <div data-testid="offer-builder">
      <BackLink onClick={onClose} label="Back to Applications" />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 className="mcl-page-title" data-testid="offer-builder-title">
            {editingLiveOffer ? 'Update Your Offer' : 'Create Funding Offer'}
          </h2>
          <p className="mcl-page-subtitle">
            {application.firstName} {application.lastName} &middot; {(application.caseType || '').replace(/_/g, ' ')}
            {originalRequestedAmount > 0 && <> &middot; requested ${formatCurrencyDisplay(originalRequestedAmount)}</>}
          </p>
        </div>
      </div>

      {frozenV1 && (
        <div
          className="mcl-card"
          style={{ marginBottom: '1.25rem', borderColor: '#fcd34d', background: 'var(--mcl-warning-bg)', padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}
          data-testid="frozen-offer-notice"
        >
          <Lock size={16} style={{ color: 'var(--mcl-warning)', flexShrink: 0, marginTop: '1px' }} />
          <div style={{ fontSize: '0.8125rem', color: '#92400e', lineHeight: 1.5 }}>
            <strong style={{ fontWeight: 700 }}>These terms are locked.</strong>{' '}
            This offer was priced before payment windows, on a schedule that fixes an
            amount for each date. Its terms may already have been agreed, so they are
            shown here exactly as stored and cannot be repriced. Notes are still yours
            to edit. To offer different terms, revoke this offer and make a new one.
          </div>
        </div>
      )}

      {editingLiveOffer && !frozenV1 && (
        <div
          className="mcl-card"
          style={{ marginBottom: '1.25rem', borderColor: '#bfdbfe', background: 'var(--mcl-info-bg)', padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}
          data-testid="live-offer-notice"
        >
          <TriangleAlert size={16} style={{ color: 'var(--mcl-primary)', flexShrink: 0, marginTop: '1px' }} />
          <div style={{ fontSize: '0.8125rem', color: '#1d4ed8', lineHeight: 1.5 }}>
            <strong style={{ fontWeight: 700 }}>You are editing a live offer.</strong>{' '}
            The plaintiff is comparing it now, and will see these terms as soon as you update.
            To take it off the table instead, go back and revoke it.
          </div>
        </div>
      )}

      <div className="mcl-offer-builder-grid">

        {/* ---------------- levers ---------------- */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', minWidth: 0 }}>

          <div className="mcl-card">
            <div className="mcl-card-header">
              <h3 className="mcl-card-header-title">Your terms for this case</h3>
              <span style={thisOfferBadge}>THIS OFFER</span>
            </div>

            {/* Presets: a starting point that names what it sets. */}
            <div style={{ padding: '1rem 1.5rem', background: '#f8fafc', borderBottom: '1px solid var(--mcl-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--mcl-text-muted)', letterSpacing: '0.04em' }}>START FROM</span>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {['low', 'medium', 'high'].map((tier) => {
                    const active = formData.riskTier === tier;
                    return (
                      <button
                        key={tier}
                        type="button"
                        onClick={() => applyPreset(tier)}
                        style={{
                          padding: '0.3rem 0.75rem', borderRadius: '6px', fontSize: '0.75rem',
                          fontWeight: active ? 700 : 600, fontFamily: 'inherit', cursor: 'pointer',
                          border: `1px solid ${active ? 'var(--mcl-warning)' : 'var(--mcl-border)'}`,
                          backgroundColor: active ? 'var(--mcl-warning-bg)' : '#ffffff',
                          color: active ? '#b45309' : 'var(--mcl-text-muted)'
                        }}
                        data-testid={`risk-tier-${tier}`}
                      >
                        {RISK_TIERS[tier].label} &middot; {RISK_TIERS[tier].rate}% / {RISK_TIERS[tier].capMultiplier.toFixed(1)}x
                      </button>
                    );
                  })}
                </div>
                <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-light)' }}>
                  Fills the fields below. Change any of them freely &mdash; the plaintiff never sees this label.
                </span>
              </div>
            </div>

            <div className="mcl-card-body">
              <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                <div className="mcl-form-group">
                  <label className="mcl-form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <DollarSign size={14} style={{ color: 'var(--mcl-success)' }} /> Advance Amount *
                  </label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)', fontWeight: 500 }}>$</span>
                    <input className="mcl-form-input" type="text" value={formatCurrencyInput(formData.advanceAmount)} onChange={(e) => handleInputChange('advanceAmount', parseCurrency(e.target.value))} placeholder="500" disabled={frozenV1} style={{ paddingLeft: '28px', fontSize: '1.1rem', fontWeight: 600 }} data-testid="input-advance-amount" />
                  </div>
                  {originalRequestedAmount > 0 && (
                    <div className="mcl-form-hint" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <span>Original request: <span style={{ fontWeight: 600, color: 'var(--mcl-success)' }}>${formatCurrencyDisplay(originalRequestedAmount)}</span></span>
                      {parseCurrency(formData.advanceAmount) !== originalRequestedAmount.toString() && (
                        <button type="button" onClick={() => handleInputChange('advanceAmount', originalRequestedAmount.toString())} style={{ ...linkButtonStyle, textDecoration: 'underline' }}>Reset</button>
                      )}
                    </div>
                  )}
                </div>
                <div className="mcl-form-group">
                  <label className="mcl-form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Calendar size={14} style={{ color: 'var(--mcl-success)' }} /> Funding Date *
                  </label>
                  <input className="mcl-form-input" type="date" value={formData.fundingDate} onChange={(e) => handleInputChange('fundingDate', e.target.value)} disabled={frozenV1} data-testid="input-funding-date" />
                </div>
              </div>

              <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', marginBottom: '1.25rem' }}>
                <div className="mcl-form-group" style={{ marginBottom: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', marginBottom: '0.4rem' }}>
                    <label className="mcl-form-label" style={{ marginBottom: 0 }}>Annualized Rate (%)</label>
                    {edited.annualizedRate && <span style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--mcl-primary)', letterSpacing: '0.03em' }}>EDITED</span>}
                  </div>
                  <input
                    className="mcl-form-input"
                    type="number"
                    value={formData.annualizedRate}
                    onChange={(e) => handleTermChange('annualizedRate', e.target.value)}
                    placeholder="32"
                    min="0"
                    max="100"
                    disabled={frozenV1}
                    style={overRateCeiling ? { borderColor: '#fcd34d', boxShadow: '0 0 0 3px rgba(217,119,6,0.08)' } : undefined}
                    data-testid="input-rate"
                  />
                  {overRateCeiling ? (
                    <div className="mcl-form-hint" style={{ color: 'var(--mcl-warning)', display: 'flex', alignItems: 'flex-start', gap: '5px' }} data-testid="rate-ceiling-warning">
                      <TriangleAlert size={13} style={{ flexShrink: 0, marginTop: '1px' }} />
                      <span>Above your {rateCeiling}% ceiling. Submit anyway, or raise it in Settings.</span>
                    </div>
                  ) : (
                    rateCeiling !== undefined && rateCeiling !== null && (
                      <div className="mcl-form-hint">Your ceiling: {rateCeiling}%</div>
                    )
                  )}
                  {edited.annualizedRate && (
                    <div className="mcl-form-hint">
                      Preset says {RISK_TIERS[formData.riskTier].rate}% &middot;{' '}
                      <button type="button" onClick={() => resetToPreset('annualizedRate')} style={linkButtonStyle} data-testid="reset-rate">Reset</button>
                    </div>
                  )}
                </div>

                <div className="mcl-form-group" style={{ marginBottom: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', marginBottom: '0.4rem' }}>
                    <label className="mcl-form-label" style={{ marginBottom: 0 }}>Cap Multiplier</label>
                    {edited.capMultiplier && <span style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--mcl-primary)', letterSpacing: '0.03em' }}>EDITED</span>}
                  </div>
                  <input
                    className="mcl-form-input"
                    type="number"
                    value={formData.capMultiplier}
                    onChange={(e) => handleTermChange('capMultiplier', e.target.value)}
                    placeholder="2.5"
                    step="0.01"
                    disabled={frozenV1}
                    style={overCapCeiling ? { borderColor: '#fcd34d', boxShadow: '0 0 0 3px rgba(217,119,6,0.08)' } : undefined}
                    data-testid="input-cap-multiplier"
                  />
                  {overCapCeiling ? (
                    <div className="mcl-form-hint" style={{ color: 'var(--mcl-warning)', display: 'flex', alignItems: 'flex-start', gap: '5px' }} data-testid="cap-ceiling-warning">
                      <TriangleAlert size={13} style={{ flexShrink: 0, marginTop: '1px' }} />
                      <span>Above your {capCeiling}x ceiling.</span>
                    </div>
                  ) : (
                    capCeiling !== undefined && capCeiling !== null && (
                      <div className="mcl-form-hint">Your ceiling: {capCeiling}x</div>
                    )
                  )}
                  {edited.capMultiplier && (
                    <div className="mcl-form-hint">
                      Preset says {RISK_TIERS[formData.riskTier].capMultiplier}x &middot;{' '}
                      <button type="button" onClick={() => resetToPreset('capMultiplier')} style={linkButtonStyle} data-testid="reset-cap-multiplier">Reset</button>
                    </div>
                  )}
                </div>

                <div className="mcl-form-group" style={{ marginBottom: 0 }}>
                  <label className="mcl-form-label">Max Term (months)</label>
                  <select className="mcl-form-input" value={formData.maxTermMonths} onChange={(e) => handleTermChange('maxTermMonths', e.target.value)} disabled={frozenV1} data-testid="input-max-term">
                    {[6, 12, 18, 24, 30, 36, 42, 48, 54, 60].map(m => <option key={m} value={m}>{m} months</option>)}
                  </select>
                  {settings.max_term_months && (
                    <div className="mcl-form-hint">Your ceiling: {settings.max_term_months} months</div>
                  )}
                </div>

                {/* v2 only. A v1 offer has no accrual interval — its schedule
                    is a list of dates, not windows — so offering the control
                    would invite a change the freeze silently discards. */}
                {!frozenV1 && (
                  <div className="mcl-form-group" style={{ marginBottom: 0 }}>
                    <label className="mcl-form-label">Accrual Interval</label>
                    <select
                      className="mcl-form-input"
                      value={formData.accrualIntervalMonths}
                      onChange={(e) => handleInputChange('accrualIntervalMonths', e.target.value)}
                      data-testid="input-accrual-interval"
                    >
                      {VALID_INTERVALS.map((m) => (
                        <option key={m} value={m}>{m === 1 ? 'Monthly' : `${m} months`}</option>
                      ))}
                    </select>
                    <div className="mcl-form-hint">
                      {repaymentBands.length} payment window{repaymentBands.length === 1 ? '' : 's'} over the term
                    </div>
                  </div>
                )}
              </div>

              {/* The cap sits beside the multiplier that computes it. */}
              <div style={{ padding: '1rem 1.25rem', borderRadius: '8px', background: '#f8fafc', border: '1px solid var(--mcl-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--mcl-text-heading)', marginBottom: '0.15rem' }}>Maximum repayment cap</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>
                    {frozenV1
                      ? 'As agreed on this offer'
                      : useAutoCapCalculation
                        ? <>{formData.capMultiplier}x the financed obligation of ${formatCurrencyDisplay(displaySummary.totalFunded)}</>
                        : 'Set by hand for this offer'}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {frozenV1 || useAutoCapCalculation ? (
                    <span style={{ fontSize: '1.375rem', fontWeight: 700, color: 'var(--mcl-success)' }} data-testid="maximum-cap">${formatCurrencyDisplay(displayCap)}</span>
                  ) : (
                    <div style={{ position: 'relative', maxWidth: '200px' }}>
                      <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)', fontWeight: 500 }}>$</span>
                      <input className="mcl-form-input" type="text" value={formatCurrencyInput(formData.maximumCap)} onChange={(e) => handleInputChange('maximumCap', parseCurrency(e.target.value))} placeholder="Custom cap" style={{ paddingLeft: '28px' }} data-testid="input-maximum-cap" />
                    </div>
                  )}
                  {!frozenV1 && (
                    <label className="mcl-checkbox-label" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={useAutoCapCalculation} onChange={(e) => setUseAutoCapCalculation(e.target.checked)} data-testid="auto-cap-toggle" /> Auto
                    </label>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ---- fees, split by who owns them ---- */}
          <div className="mcl-card">
            <div className="mcl-card-header">
              <h3 className="mcl-card-header-title">Fees on this offer</h3>
            </div>
            <div className="mcl-card-body">
              {/* v2 only. A v1 offer predates the fee model entirely — it is
                  itemized by construction — and the freeze discards any
                  change to it, so the choice is not offered. */}
              {!frozenV1 && (
              <div className="mcl-radio-group" style={{ marginBottom: '1.25rem' }} role="radiogroup" aria-label="Fee model" data-testid="fee-model-group">
                {VALID_FEE_MODELS.map((model) => (
                  <label key={model} className="mcl-radio-label">
                    <input
                      type="radio"
                      name="fee-model"
                      checked={formData.feeModel === model}
                      onChange={() => handleInputChange('feeModel', model)}
                      data-testid={`fee-model-${model}`}
                    />
                    {model === 'itemized' ? 'Itemized (broker + application)' : 'Flat processing fee'}
                  </label>
                ))}
              </div>
              )}

              <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                {(frozenV1 || formData.feeModel === 'itemized') && renderStandingFee({
                  field: 'brokerFee',
                  testId: 'input-broker-fee',
                  label: 'Broker Fee',
                  // A frozen offer shows the fee it was WRITTEN with, not the
                  // funder's fee today. Recomputing it here would put a number
                  // on screen the server will never store.
                  value: frozenV1
                    ? formatCurrencyInput(displaySummary.broker.toFixed(2))
                    : feeOverride.brokerFee
                      ? formatCurrencyInput(formData.brokerFee)
                      : formatCurrencyInput(calculatedBrokerFee.toFixed(2)),
                  hint: frozenV1
                    ? 'As agreed on this offer'
                    : brokerFeePct === 0
                      ? 'Waived ($0)'
                      : (brokerFeeCap ? `${(brokerFeePct * 100).toFixed(1)}% of advance (max $${brokerFeeCap.toLocaleString()})` : `${(brokerFeePct * 100).toFixed(1)}% of advance`),
                  seed: calculatedBrokerFee
                })}

                {(frozenV1 || formData.feeModel === 'itemized') && renderStandingFee({
                  field: 'applicationFee',
                  testId: 'input-application-fee',
                  label: 'Application Fee',
                  value: frozenV1
                    ? formatCurrencyInput(displaySummary.appFee.toFixed(2))
                    : feeOverride.applicationFee
                      ? formatCurrencyInput(formData.applicationFee)
                      : formatCurrencyInput(defaultApplicationFee.toFixed(2)),
                  hint: frozenV1
                    ? 'As agreed on this offer'
                    : defaultApplicationFee === 0 ? 'Waived ($0)' : `Flat $${defaultApplicationFee}`,
                  seed: defaultApplicationFee
                })}

                {!frozenV1 && formData.feeModel === 'flat' && renderStandingFee({
                  field: 'processingFee',
                  testId: 'input-processing-fee',
                  label: 'Processing Fee',
                  value: formatCurrencyInput(resolvedFees.processing_fee.toFixed(2)),
                  hint: `Flat $${resolvedFees.processing_fee}`,
                  seed: resolvedFees.processing_fee
                })}

                <div className="mcl-form-group" style={{ marginBottom: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '0.4rem' }}>
                    <label className="mcl-form-label" style={{ marginBottom: 0, color: 'var(--mcl-text-muted)' }}>Platform Fee</label>
                    <span style={platformBadge}><Lock size={9} /> MYCASELOAN</span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-light)', fontWeight: 500 }}>$</span>
                    <input
                      className="mcl-form-input"
                      type="text"
                      value={formatCurrencyInput(displaySummary.platformFee || 0)}
                      readOnly
                      style={{ paddingLeft: '28px', paddingRight: '2.25rem', backgroundColor: 'var(--mcl-border-light)', color: 'var(--mcl-text-light)', cursor: 'not-allowed' }}
                      data-testid="input-platform-fee"
                    />
                    <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-light)', display: 'inline-flex' }}>
                      <Lock size={13} />
                    </span>
                  </div>
                  <div className="mcl-form-hint">
                    {platformFeeWaived ? 'Waived for this firm' : 'Tier-based on advance amount'}
                  </div>
                </div>
              </div>

              {/* v2 only. A v1 offer accrues on the full financed obligation
                  by construction, and the freeze discards any change. */}
              {!frozenV1 && (
              <div
                data-testid="fees-accrue-group"
                style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
                         marginTop: '0.9rem', paddingTop: '0.9rem',
                         borderTop: '1px solid var(--mcl-border-light)' }}
              >
                <input
                  type="checkbox"
                  id="fees-accrue-interest"
                  checked={formData.feesAccrueInterest}
                  onChange={(e) => handleInputChange('feesAccrueInterest', e.target.checked)}
                  data-testid="toggle-fees-accrue"
                />
                <label htmlFor="fees-accrue-interest" style={{ marginBottom: 0 }}>
                  <span style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>
                    These fees accrue interest
                  </span>
                  <span className="mcl-form-hint" style={{ display: 'block' }}>
                    {formData.feesAccrueInterest
                      ? 'Interest runs on the advance plus all fees'
                      : 'Fees ride on top, uncompounded'}
                  </span>
                </label>
              </div>
              )}
            </div>
          </div>

          <div className="mcl-card">
            <div className="mcl-card-header"><h3 className="mcl-card-header-title">Notes</h3></div>
            <div className="mcl-card-body">
              <textarea className="mcl-form-textarea" value={formData.notes} onChange={(e) => handleInputChange('notes', e.target.value)} placeholder="Additional notes about this offer..." rows={3} data-testid="input-notes" />
            </div>
          </div>
        </div>

        {/* ---------------- consequences ---------------- */}
        <div className="mcl-offer-builder-rail">
          <div className="mcl-card" style={{ borderColor: '#a7f3d0', overflow: 'hidden' }}>
            <div className="mcl-card-header" style={{ borderBottomColor: '#a7f3d0', background: 'var(--mcl-success-bg)', padding: '0.85rem 1.25rem' }}>
              <h3 className="mcl-card-header-title" style={{ fontSize: '0.875rem', color: '#065f46' }}>
                {frozenV1 ? 'What you agreed' : 'What you are offering'}
              </h3>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.8125rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--mcl-text-muted)' }}>Advance to plaintiff</span>
                  <span style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }} data-testid="summary-advance">${formatCurrencyDisplay(displaySummary.advance)}</span>
                </div>
                <div style={{ paddingLeft: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.75rem', borderLeft: '2px solid var(--mcl-border-light)' }}>
                  {frozenV1 || formData.feeModel === 'itemized' ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--mcl-text-light)' }}>Broker &middot; yours</span>
                        <span style={{ color: 'var(--mcl-text-muted)' }}>${formatCurrencyDisplay(displaySummary.broker)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--mcl-text-light)' }}>Application &middot; yours</span>
                        <span style={{ color: 'var(--mcl-text-muted)' }}>${formatCurrencyDisplay(displaySummary.appFee)}</span>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--mcl-text-light)' }}>Processing &middot; yours</span>
                      <span style={{ color: 'var(--mcl-text-muted)' }}>${formatCurrencyDisplay(displaySummary.processingFee)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--mcl-text-light)' }}>Platform &middot; MyCaseLoan</span>
                    <span style={{ color: 'var(--mcl-text-muted)' }}>${formatCurrencyDisplay(displaySummary.platformFee || 0)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--mcl-text-muted)' }}>Total fees</span>
                  <span style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }} data-testid="summary-total-fees">${formatCurrencyDisplay(displaySummary.totalFees)}</span>
                </div>
              </div>
              <div style={{ marginTop: '1rem', paddingTop: '0.85rem', borderTop: '1px solid var(--mcl-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#065f46' }}>Financed obligation</span>
                <span style={{ fontSize: '1.375rem', fontWeight: 700, color: '#065f46' }} data-testid="summary-total-funded">${formatCurrencyDisplay(displaySummary.totalFunded)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--mcl-text-muted)' }}>Most they can ever repay</span>
                <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--mcl-text-heading)' }} data-testid="summary-maximum-cap">${formatCurrencyDisplay(displayCap)}</span>
              </div>
              {curve && (
                <div style={{ fontSize: '0.6875rem', color: 'var(--mcl-text-light)', marginTop: '0.2rem', textAlign: 'right' }}>
                  {formData.capMultiplier}x the obligation &middot; {curve.capReached ? `reached at ${curve.lastMonths} months` : `not reached inside the ${formData.maxTermMonths}mo term`}
                </div>
              )}
            </div>
          </div>

          {/* A v1 offer's schedule is a list of DATES, each with the amount
              owed on that day — not payment windows. Rendered from the stored
              document in its own shape, because converting it would show the
              funder terms the server will never store, and because the terms
              on it may already have been agreed. */}
          {frozenV1 && storedSchedule.length > 0 && (
            <div className="mcl-card" style={{ overflow: 'hidden' }} data-testid="v1-repayment-schedule">
              <div className="mcl-card-header" style={{ padding: '0.85rem 1.25rem' }}>
                <h3 className="mcl-card-header-title" style={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <FileText size={15} /> What they repay, as agreed
                </h3>
              </div>
              <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.55rem', fontSize: '0.75rem' }}>
                {(scheduleExpanded ? storedSchedule : storedSchedule.slice(0, 3)).map((row, index) => (
                  <div key={`${row.date}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }} data-testid={`v1-schedule-row-${index}`}>
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }} data-testid="v1-schedule-date">
                        If paid on or before {row.date}
                      </div>
                      {row.is_cap && (
                        <div style={{ color: 'var(--mcl-warning)', fontSize: '0.6875rem', fontWeight: 600, marginTop: '0.1rem' }}>(cap)</div>
                      )}
                    </div>
                    <span style={{ fontWeight: 600, color: 'var(--mcl-text-heading)', whiteSpace: 'nowrap' }} data-testid="v1-schedule-amount">${formatCurrencyDisplay(row.amount)}</span>
                  </div>
                ))}
                {storedSchedule.length > 3 && (
                  <button type="button" onClick={() => setScheduleExpanded(!scheduleExpanded)} style={{ ...linkButtonStyle, marginTop: '0.2rem', alignSelf: 'flex-start' }} data-testid="toggle-v1-schedule">
                    {scheduleExpanded ? 'Show fewer dates' : `View all ${storedSchedule.length} dates`}
                  </button>
                )}
              </div>
            </div>
          )}

          {curve && (
            <div className="mcl-card" style={{ overflow: 'hidden' }}>
              <div className="mcl-card-header" style={{ padding: '0.85rem 1.25rem' }}>
                <h3 className="mcl-card-header-title" style={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <FileText size={15} /> What they repay, over time
                </h3>
              </div>
              <div style={{ padding: '1.25rem' }}>
                <svg viewBox="0 0 320 128" style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="Repayment growing toward the maximum cap">
                  <line x1="10" y1="10" x2="310" y2="10" stroke="#fcd34d" strokeWidth="1.5" strokeDasharray="4 4" />
                  <text x="310" y="6" textAnchor="end" fontSize="8" fontWeight="700" fill="#b45309">CAP ${formatCurrencyDisplay(maximumCap)}</text>
                  <path d={curve.area} fill="var(--mcl-success-bg)" />
                  <path d={curve.line} stroke="var(--mcl-success)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                  <circle cx={curve.first.x} cy={curve.first.y} r="3.5" fill="var(--mcl-success)" />
                  <circle cx={curve.last.x} cy={curve.last.y} r="3.5" fill="var(--mcl-success)" />
                  <text x="10" y="112" fontSize="8" fontWeight="600" fill="#94a3b8">{curve.firstMonths}mo</text>
                  <text x="310" y="112" textAnchor="end" fontSize="8" fontWeight="600" fill="#94a3b8">{curve.lastMonths}mo</text>
                </svg>
                <div style={{ marginTop: '0.85rem', display: 'flex', flexDirection: 'column', gap: scheduleExpanded ? '1rem' : '0.55rem', fontSize: '0.75rem' }}>
                  {scheduleExpanded
                    ? bandYearGroups.map(({ year, items }) => (
                        <div key={year}>
                          <h4
                            data-testid="band-year-group"
                            style={{
                              fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase',
                              letterSpacing: '0.04em', color: 'var(--mcl-text-light)',
                              margin: '0 0 0.4rem', paddingBottom: '0.25rem',
                              borderBottom: '1px solid var(--mcl-border-light)'
                            }}
                          >
                            Year {year + 1}
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                            {items.map(({ band, index }) => renderBandRow(band, index))}
                          </div>
                        </div>
                      ))
                    : repaymentBands.slice(0, 3).map((item, index) => renderBandRow(item, index))}
                </div>
                {repaymentBands.length > 3 && (
                  <button type="button" onClick={() => setScheduleExpanded(!scheduleExpanded)} style={{ ...linkButtonStyle, marginTop: '0.75rem' }} data-testid="toggle-bands">
                    {scheduleExpanded ? 'Show fewer payment windows' : `View all ${repaymentBands.length} payment windows`}
                  </button>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {frozenV1 ? (
              <button className="mcl-btn mcl-btn-outline" onClick={() => handleSaveOffer(false)} disabled={loading} data-testid="save-notes-button">
                <Save size={16} /> {loading ? 'Saving...' : 'Save Notes'}
              </button>
            ) : (
              <>
                <button className="mcl-btn mcl-btn-success" onClick={() => handleSaveOffer(true)} disabled={loading} data-testid="submit-offer-button">
                  <Send size={16} /> {loading ? 'Saving...' : (editingLiveOffer ? 'Update Offer' : 'Submit Offer')}
                </button>
                <button className="mcl-btn mcl-btn-outline" onClick={() => handleSaveOffer(false)} disabled={loading} data-testid="save-draft-button">
                  <Save size={16} /> Save as Draft
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FunderOfferBuilder;
